import { RawDraftContentState } from 'draft-js';
import { isEqual, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { getAllDropdownLists } from '../../../admin/dropdown-lists/store';
import { IconButton } from '../../../shared/button/IconButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { Icon } from '../../../shared/icon/Icon';
import { CaretDown, CaretSide, CheckList, Tick } from '../../../shared/icons';
import { PopoutWysiwygModal } from '../../../shared/modal/PopoutWysiwygModal';
import { Toggle } from '../../../shared/toggle';
import { IconTooltip, OverflowTooltip } from '../../../shared/tooltip';
import { isEmpty } from '../../../shared/wysiwyg/WYSIWYG';
import { SingleField } from '../fields/SingleField';
import { SignOffConversationIcon } from '../sign-off';
import { addTransactionFieldCustomChild, createTransactionCoverageField, getFieldSignOffNotes, getIsFirstTimelineEntry, getOpenFieldsAndSections, getShowUpdatedOnly, toggleOpenFieldSection, updateCustomToggleLabel, updateTransactionCoverageField } from '../store';
import { TransactionCoverage, TransactionCoverageField, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

const { amethyst, grey, french, primary, disabledGrey } = styles;

interface TransactionCoverageProps {
    transactionCoverage: TransactionCoverage;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    nettingEngineFields: string[];
}

export const TransactionCoverageSection: React.FC<TransactionCoverageProps> = ({ transactionCoverage, isEditing, isUpdating, fieldsUpdated, nettingEngineFields }) => {
    const [autoFocus, setAutoFocus] = useState<boolean>(false);
    const [allConEffectiveValue, setAllConEffectiveValue] = useState<string[] | null>(null);
    const [showSetAllLayer, setShowSetAllLayer] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const isFirstTimelineEntry = useAppSelector(getIsFirstTimelineEntry);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const showUpdatedOnly = useAppSelector(getShowUpdatedOnly);

    const sectionId = 'transactionCoverage';
    const { standardOptions, customOptions, other } = transactionCoverage;
    const dropdownOptions = useAppSelector(getAllDropdownLists);
    const yesNoUnclearDropdowns = dropdownOptions.find(({ name }) => name === 'YesNoUnclear')!.options.map(value => ({ label: value, value }));

    const signOffConversation = useAppSelector(getFieldSignOffNotes(sectionId));

    const updateValue = useCallback((
        fieldId: 'standardOptions' | 'customOptions',
        value: boolean | RawDraftContentState | null | string[],
        index: number,
        key: keyof TransactionCoverageField
    ) => {
        dispatch(updateTransactionCoverageField(value, fieldId, index, key));
    }, [dispatch]);

    const fieldUpdatedByInstance = useCallback((fieldId: 'standardOptions' | 'customOptions', index: number, columnIndex?: number) => !!fieldsUpdated.find(field => isEqual({ fieldId: field.fieldId, sectionId: field.sectionId, index: field.index, columnIndex: field.columnIndex }, { fieldId, sectionId, index, columnIndex })), [fieldsUpdated, sectionId]);
    const getIsDisabled = useCallback((fieldId: 'standardOptions' | 'customOptions', index: number, columnIndex?: number) => !isEditing && !(isUpdating && fieldUpdatedByInstance(fieldId, index, columnIndex)), [isUpdating, isEditing, fieldUpdatedByInstance]);
    const shouldShowFieldUpdated = useCallback((fieldId: 'standardOptions' | 'customOptions', index: number, columnIndex?: number) => fieldUpdatedByInstance(fieldId, index, columnIndex) && !isUpdating && !isEditing && !isFirstTimelineEntry, [isUpdating, isEditing, fieldUpdatedByInstance, isFirstTimelineEntry]);
    const disabled = !isEditing && !isUpdating;

    const updateAll = (checked: boolean) => {
        standardOptions.filter(({ label }) => label !== 'Other').forEach((_, index) => updateValue('standardOptions', checked, index, 'includedInOpinion'));
        customOptions.forEach((_, index) => updateValue('customOptions', checked, index, 'includedInOpinion'));
    };

    const updateConEffective = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null, fieldId: 'standardOptions' | 'customOptions', index: number) => {
        let value: string[] | null = null;
        if (!isNull(dropdownValue)) {
            value = [(dropdownValue as DropdownOption).value];
        }
        updateValue(fieldId, value, index, 'conEffective');
    }, [updateValue]);

    const boxShadowColour = useCallback((showFieldUpdated: boolean) => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return grey;
    }, []);

    const onColour = useCallback((showFieldUpdated: boolean) => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return french;
    }, []);

    const columnUpdatedColor = useCallback((showFieldUpdated: boolean) => {
        if (showFieldUpdated) {
            return amethyst;
        }
        return disabled ? disabledGrey : primary;
    }, [disabled]);

    const updateCustomLabel = useCallback((value: string, index: number) => dispatch(updateCustomToggleLabel(sectionId, index, value)), [dispatch, sectionId]);

    const addCustomOption = useCallback(() => {
        const newField = createTransactionCoverageField('');
        const newCustomOptions = [...customOptions, newField];
        dispatch(addTransactionFieldCustomChild(sectionId, newCustomOptions));
        setAutoFocus(true);
    }, [dispatch, sectionId, customOptions]);

    const allSelected = useMemo(() => [...standardOptions, ...customOptions].filter(({ label }) => label !== 'Other').map(({ includedInOpinion }) => includedInOpinion).every(value => !!value), [standardOptions, customOptions]);

    const nettingEngineTooltip = 'Any products selected in this section are considered valid in the Netting Engine analysis';

    const allConEffectiveDropdownValue: DropdownOption[] | null = allConEffectiveValue?.map(conEffective => ({ label: conEffective, value: conEffective })) || null;

    const setConEffectiveForAllRows = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value: string[] | null = null;
        if (!isNull(dropdownValue)) {
            value = [(dropdownValue as DropdownOption).value];
        }
        setAllConEffectiveValue(value);
        standardOptions.filter(({ label }) => label !== 'Other').forEach((_, i) => updateConEffective(dropdownValue, 'standardOptions', i));
        customOptions.forEach((_, i) => updateConEffective(dropdownValue, 'customOptions', i));
    }, [standardOptions, updateConEffective, customOptions]);

    const notesTooltip = useCallback((notes: RawDraftContentState | null, color: string) => {
        const emptyNotes = isNull(notes) || isEmpty(notes);
        return (
            <div className={styles.notesWrapper}>
                {!emptyNotes && <div className={styles.notesWrapperIcon}><Icon icon={Tick} fontSize={14} color='green' /></div>}
                <IconTooltip icon={CheckList} fontSize={28} content='Notes / Conditions' iconColor={color} />
            </div>
        );
    }, []);

    const getShouldDisplayField = useCallback((fieldId: 'standardOptions' | 'customOptions', index: number) => {
        if (!showUpdatedOnly || isFirstTimelineEntry) {
            return true;
        }
        const includedInOpinionUpdated = fieldUpdatedByInstance(fieldId, index, 0);
        const conEffectiveUpdated = fieldUpdatedByInstance(fieldId, index, 1);
        const notesUpdated = fieldUpdatedByInstance(fieldId, index, 2);
        return [includedInOpinionUpdated, conEffectiveUpdated, notesUpdated].some(valueUpdated => valueUpdated);
    }, [showUpdatedOnly, isFirstTimelineEntry, fieldUpdatedByInstance]);

    return (
        <SectionWrapper id={sectionId} label='Transaction Coverage' isEditing={isEditing} isUpdating={isUpdating} nettingEngineFields={nettingEngineFields} nettingEngineTooltip={nettingEngineTooltip}>
            <div className={styles.transactionCoverageSectionWrapper}>
                <div className={styles.transactionCoverageWrapper}>
                    <div className={styles.transactionCoverageHeaderWrapper}>
                        <div className={styles.addOptionWrapper}>
                            <div className={styles.addOptionLabel}>Add another option</div>
                            <div className={styles.addOptionButton}><PlusButton onClick={addCustomOption} fontSize={16} disabled={disabled} /></div>
                            {!isUndefined(signOffConversation) && <SignOffConversationIcon signOffConversation={signOffConversation} id={sectionId} />}
                        </div>
                    </div>
                    <div className={styles.transactionCoverageTableHeaderWrapper}>
                        <div className={styles.transactionCoverageLabel}>Coverage Type</div>
                        <div className={styles.transactionCoverageToggleWrapper}>
                            Include in Opinion
                        </div>
                        <div className={styles.transactionCoverageToggleWrapper}>
                            <div>CON Effective</div>
                        </div>
                        <IconButton icon={showSetAllLayer ? CaretDown : CaretSide} fontSize={18} onClick={() => setShowSetAllLayer(!showSetAllLayer)} />
                    </div>
                    {showSetAllLayer && <div className={styles.transactionCoverageTableHeaderWrapper}>
                        <div className={styles.transactionCoverageLabel}></div>
                        <div className={styles.transactionCoverageToggleWrapper}>
                            <div style={{ marginRight: '5px' }}>Include all</div>
                            <Toggle onChange={updateAll} checked={allSelected} disabled={disabled} />
                        </div>
                        <div className={styles.transactionCoverageToggleWrapper}>
                            <Dropdown
                                value={allConEffectiveDropdownValue}
                                onChange={value => setConEffectiveForAllRows(value)}
                                options={yesNoUnclearDropdowns}
                                disabled={disabled}
                            />
                        </div>
                    </div>}
                    <div className={styles.transactionCoverageOptionsWrapper} data-testid='transaction-coverage-standard-options-wrapper'>
                        {standardOptions.map(({ includedInOpinion, conEffective, label, notes }, index) => {
                            const fieldId = 'standardOptions';
                            const isDisabled = getIsDisabled(fieldId, index);
                            const showFieldUpdated = (columnIndex: number) => shouldShowFieldUpdated(fieldId, index, columnIndex);
                            const conEffectiveDropdownValue: DropdownOption[] | null = conEffective?.map(conEffective => ({ label: conEffective, value: conEffective })) || null;
                            const shouldDisplayField = getShouldDisplayField(fieldId, index);

                            if (!shouldDisplayField) {
                                return null;
                            }

                            return (
                                <div className={styles.toggleOptionWrapper} key={index}>
                                    <div className={styles.transactionCoverageLabel}>
                                        <OverflowTooltip overlayText={label} trigger='click' />
                                    </div>
                                    <div className={styles.transactionCoverageToggleWrapper}>
                                        <Toggle
                                            onChange={checked => updateValue(fieldId, checked, index, 'includedInOpinion')}
                                            checked={includedInOpinion || false}
                                            disabled={isDisabled}
                                            boxShadowColour={boxShadowColour(showFieldUpdated(0))}
                                            onColour={onColour(showFieldUpdated(0))}
                                        />
                                    </div>
                                    <div className={styles.transactionCoverageToggleWrapper}>
                                        <Dropdown
                                            disabled={isDisabled}
                                            value={conEffectiveDropdownValue}
                                            onChange={value => updateConEffective(value, fieldId, index)}
                                            options={yesNoUnclearDropdowns}
                                            controlBorderColor={columnUpdatedColor(showFieldUpdated(1))}
                                        />
                                    </div>
                                    <div className={styles.transactionCoverageNotesWrapper}>
                                        <PopoutWysiwygModal
                                            triggerElement={notesTooltip(notes, columnUpdatedColor(showFieldUpdated(2)))}
                                            content={notes}
                                            updateContent={val => updateValue(fieldId, val, index, 'notes')}
                                            disabled={isDisabled}
                                            hideToolbar={isDisabled}
                                            headerLabel={label}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                        {customOptions.map(({ label, includedInOpinion, conEffective, notes }, index) => {
                            const fieldId = 'customOptions';
                            const isDisabled = getIsDisabled(fieldId, index);
                            const showFieldUpdated = (columnIndex: number) => shouldShowFieldUpdated(fieldId, index, columnIndex);
                            const conEffectiveDropdownValue: DropdownOption[] | null = conEffective?.map(conEffective => ({ label: conEffective, value: conEffective })) || null;
                            return (
                                <div key={index} className={styles.toggleOptionWrapper}>
                                    {disabled ?
                                        <div className={styles.transactionCoverageLabel}>
                                            <OverflowTooltip overlayText={label} trigger='click' />
                                        </div>
                                        : <input
                                            className={styles.toggleOptionInput}
                                            value={label}
                                            onChange={e => updateCustomLabel(e.target.value, index)}
                                            data-testid={`transaction-coverage-custom-option-input-${index}`}
                                            autoFocus={autoFocus}
                                        />
                                    }
                                    <div className={styles.transactionCoverageToggleWrapper}>
                                        <Toggle
                                            onChange={checked => updateValue(fieldId, checked, index, 'includedInOpinion')}
                                            checked={includedInOpinion}
                                            disabled={isDisabled}
                                            boxShadowColour={boxShadowColour(showFieldUpdated(0))}
                                            onColour={onColour(showFieldUpdated(0))}
                                        />
                                    </div>
                                    <div className={styles.transactionCoverageToggleWrapper}>
                                        <Dropdown
                                            value={conEffectiveDropdownValue}
                                            onChange={value => updateConEffective(value, fieldId, index)}
                                            options={yesNoUnclearDropdowns}
                                            disabled={isDisabled}
                                            controlBorderColor={columnUpdatedColor(showFieldUpdated(1))}
                                        />
                                    </div>
                                    <PopoutWysiwygModal
                                        triggerElement={notesTooltip(notes, columnUpdatedColor(showFieldUpdated(2)))}
                                        content={notes}
                                        updateContent={val => updateValue(fieldId, val, index, 'notes')}
                                        disabled={isDisabled}
                                        hideToolbar={isDisabled}
                                        headerLabel={label}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <SingleField
                    field={other}
                    fieldId='other'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
            </div>
        </SectionWrapper>
    );
};
